import { useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { groupBy, map, path, pipe, prop, sortBy, toPairs, uniq } from 'ramda';
import { useResizeObserver } from '@namespace/device';
import { UserContext } from '@namespace/user';
import { useSportsbookStore } from '../../../store';
import { MarketsGroup } from '../../MarketsGroup';
import { MarketGroupsTabs } from '../MarketGroupsTabs';
import { getMarketsByGroup } from '../utils';

import styles from './index.module.css';

export const EventMarkets = ({ eventMarketsIds, serviceId, eventId = '' }) => {
  const [state] = useContext(UserContext);
  const { settings } = state;
  const { lineViewType: lineView } = settings;
  const [activeMarket, setActiveMarket] = useState(null);
  const [screenWidth] = useResizeObserver();
  const allMarkets = useSportsbookStore('markets');

  const eventMarkets = useMemo(
    () =>
      eventMarketsIds?.reduce(
        (acc, marketId) =>
          allMarkets[marketId] ? [...acc, allMarkets[marketId]] : acc,
        []
      ) ?? [],
    [eventMarketsIds, allMarkets]
  );

  const allMarketsTemplateIds = useMemo(
    () => pipe(map(prop('marketTemplateId')), uniq)(eventMarkets),
    [eventMarkets]
  );

  const marketsByGroup = useMemo(() => {
    if (activeMarket) {
      const { marketGroupMarkets } = activeMarket;

      return getMarketsByGroup(eventMarkets, marketGroupMarkets);
    }

    return eventMarkets;
  }, [activeMarket, eventMarkets]);

  const marketsByName = useMemo(
    () =>
      pipe(
        groupBy(prop('marketName')),
        toPairs,
        sortBy(path([1, 0, 'marketOrder'])) // the field is a string with number inside that exceeds MAX_SAFE_INTEGER and thus cannot be converted to number and correctly compared as number
      )(marketsByGroup),
    [marketsByGroup]
  );

  const [firstColumnMarkets, secondColumnMarkets] = useMemo(
    () =>
      marketsByName.reduce(
        (partitions, market, index) => {
          partitions[index % 2].push(market);
          return partitions;
        },
        [[], []]
      ),
    [marketsByName]
  );

  useEffect(() => {
    if (!marketsByGroup.length) {
      setActiveMarket(null);
    }
  }, [marketsByGroup.length]);

  return (
    <div className={clsx(styles.marketsWrapper, lineView && styles[lineView])}>
      {allMarketsTemplateIds.length > 0 && (
        <MarketGroupsTabs
          dataRole="event-markets-filter"
          eventMarkets={eventMarkets}
          serviceId={serviceId}
          activeMarket={activeMarket}
          setActiveMarket={setActiveMarket}
          allMarketsTemplateIds={allMarketsTemplateIds}
        />
      )}
      <div
        data-role="event-markets-container"
        className={clsx(styles.markets, lineView && styles[lineView])}
      >
        {screenWidth <= 1671 || marketsByName.length <= 1 ? (
          marketsByName.map((marketByName) => (
            <MarketsGroup
              key={marketByName[0]}
              title={marketByName[0]}
              listOfMarkets={marketByName[1]}
              lineView={lineView}
              eventId={eventId}
            />
          ))
        ) : (
          <div className={styles.twoColumns}>
            <div className={clsx(styles.column, styles[lineView])}>
              {firstColumnMarkets.map((marketByName) => (
                <MarketsGroup
                  key={marketByName[0]}
                  title={marketByName[0]}
                  listOfMarkets={marketByName[1]}
                  lineView={lineView}
                  eventId={eventId}
                />
              ))}
            </div>
            <div className={clsx(styles.column, styles[lineView])}>
              {secondColumnMarkets.map((marketByName) => (
                <MarketsGroup
                  key={marketByName[0]}
                  title={marketByName[0]}
                  listOfMarkets={marketByName[1]}
                  lineView={lineView}
                  eventId={eventId}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
